/**
 * @file SearchBills.js
 * @version 1.32.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-04
 * @modified 2026-10-21
 *
 * 1.30.0 (2026-10-14, Cam — PO Reference and Stock): the free-text search
 * reaches a bill's PO Reference (the PO number an invoice quoted, kept as
 * text when that PO was already billed), shown under the PO column; a Stock
 * badge beside the bill number; a Stock toggle in the filter row (off by
 * default; on lists only Stock bills — persisted, cleared by Clear Filters,
 * counted as a filter); and a bulk "Stock…" change (Mark as Stock / Clear
 * Stock, POST /api/bills/stock — never sent to Xero). Before their migration
 * the toggle and the bulk change are greyed with the reason (the server's
 * `local_fields_ready`) and the filter narrows nothing.
 *
 * 1.29.0 (2026-10-12, Cam — Bulk Add Bills): "Bulk Add Bills" beside Add Bill;
 * and every time the list loads, a strip for every user while any possible
 * duplicate bills are open (DuplicateBillsNotice — pairs saved anyway from
 * Bulk Add Bills, and the nightly duplicate check's), with "Review" opening
 * both bills side by side to open, void / archive, or mark not a duplicate.
 * The list reloads when that window closes.
 *
 * 1.25.1 (2026-10-07): a third-party haulage link (utils/billHaulageLink) shows
 * in the PO column as "Haulage for PO-…" (and "(haulage)" in the export).
 *
 * 1.26.0 (2026-10-08, Cam): "Update PO from this bill…" in each row's menu
 * (UpdatePoFromBillDialog) — a bill sets its POs' costs only when the user
 * chooses. Greyed with the reason when nothing differs (the row's
 * po_cost_differences) or the bill is voided, deleted or a credit
 * (utils/billCostWriteBack.poCostRowBlock); the list reloads when it closes.
 *
 * 1.27.0 (2026-10-08, Cam — supplier credits): "Add Supplier Credit" beside
 * Add Bill (Add Bill in its credit mode — utils/supplierCredits); each row's
 * menu has "Add credit against this bill" (the supplier and the bill filled
 * in) and "Allocate to bills…" on a credit / "Apply a credit…" on a bill
 * (SupplierCreditAllocationModal), greyed with the reason where they cannot
 * apply; "Update PO from this credit…" on a credit naming POs. The list
 * reloads when the window closes.
 *
 * 1.28.0 (2026-10-08, Cam): a supplier credit never changes a PO's cost —
 * "Update PO from this bill…" is greyed on a credit with that reason
 * (utils/billCostWriteBack.poCostRowBlock); "Update PO from this credit…" is
 * gone.
 *
 * 1.21.0 (2026-10-03): "Delivered, not billed" (DeliveredNotBilledPanel) —
 * POs delivered with no bill yet, each a click from starting its bill; a bill
 * entered here shows whether it is in Xero yet beside its status.
 *
 * 1.21.1 (2026-10-03, review): bulk delete says why a bill was not deleted
 * (a bill in Xero now comes out of Xero first, and Xero can refuse).
 *
 * 1.22.0 (2026-10-05): a Queried toggle beside Archived (queried bills only;
 * persisted, cleared by Clear Filters, counted as a filter); a Status row
 * action (BillStatusDialog) — a bill's status and Queried, which on a bill in
 * Xero reach Xero.
 *
 * 1.23.0 (2026-10-05): a VOIDED bill's Edit and Status open the voided window
 * (VoidedDocumentDialog): it cannot be edited or restored, here or in Xero —
 * raise a copy, or email accounts. Edit refuses a voided bill even when it
 * never reached Xero.
 *
 * 1.24.0 (2026-10-05): Edit follows utils/billEdit (the server's rule) — with
 * editing bills in Xero switched on (GET /api/bills/push-settings
 * edit_in_xero_enabled), a bill in Xero opens for editing, approved ones for
 * holders of 'Edit posted documents'; otherwise disabled with the reason.
 * 1.24.0 (2026-10-05, Jade's feedback): statuses read Draft, Approved, Paid —
 * the badge, the Status column filter, the export and the bulk change
 * (utils/billStatus.billStatusLabel). Display only: stored values and Xero
 * are unchanged. The bulk change offers Draft or Approved.
 *
 * 1.31.0 (2026-10-15, Cam): the bulk status change asks once about bills
 * dated in a month that should be locked in Xero by now.
 *
 * 1.31.1 (2026-10-20, Cam): the expanded lines show each quantity in its own
 * unit with the tonnes beneath, and an On the PO column with the PO line's
 * supplied (else ordered) quantity the same way (QtyWithTonnes); the table
 * export carries the text.
 *
 * 1.32.0 (2026-10-21, Cam): the expanded lines drop the Item # column — the
 * item code is a badge left of the words (ItemCodeDescription) in a column
 * now called Purchase description. Qty and On the PO keep Cam's widths
 * (TableStyles.css, renumbered). The export keeps the item code as its own
 * column.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchBills.css';
import './TableStyles.css';
import Dropdown from './Dropdown';
import LastXeroSync from './LastXeroSync';
import Input from './Input';
import DateInput from './DateInput';
import DateRangeFilter from './DateRangeFilter';
import BillModal from './BillModal';
import PurchaseOrderModal from './PurchaseOrderModal';
import ConfirmDialog from './ConfirmDialog';
import ReactDOM from 'react-dom';
import PaginationControls from './PaginationControls';
import LoadingBar from './LoadingBar';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import { formatVatWithRate } from '../utils/taxTypes';
import { formatPrice } from '../utils/currencyFormatter';
import QtyWithTonnes from './QtyWithTonnes';
import ItemCodeDescription from './ItemCodeDescription';
import { quantityWithTonnesText, qtyFactsOf } from '../utils/qtyWithTonnes';
import { useSlideOutButton } from './SlideOutButton';
import { useDragSelect } from '../hooks/useDragSelect';
import TickCheckbox from './TickCheckbox';
import BillStatusDialog from './BillStatusDialog';
import usePriorMonthConfirm, { isPriorMonthConfirm, PRIOR_MONTH_CONFIRMED } from '../hooks/usePriorMonthConfirm';
import { priorMonthQuestionForMany } from '../utils/xeroLockMonth';
import UpdatePoFromBillDialog from './UpdatePoFromBillDialog';
import { poCostRowBlock } from '../utils/billCostWriteBack';
import VoidedDocumentDialog from './VoidedDocumentDialog';
import { isVoided } from '../utils/voidedDocument';
import { statusChangeAllowed, billStatusLabel } from '../utils/billStatus';
import { billEditMode } from '../utils/billEdit';
import usePersistedState from '../hooks/usePersistedState';
import RowActionsMenu from './RowActionsMenu';
import DocumentOutputModal from './DocumentOutputModal';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import ExportTableButton from './ExportTableButton';
import useResizableColumns from '../hooks/useResizableColumns';
import './ResizableColumns.css';
import EntityLink from './EntityLink';
import { useEntityModal } from './EntityModalProvider';
import { useReveal, revealFilters } from '../utils/revealInSearch';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import DeliveredNotBilledPanel from './DeliveredNotBilledPanel';
import { billPushState } from '../utils/billPushStatus';
import { billPoLabels, haulageForLabel } from '../utils/billHaulageLink';
import './BillPushStatus.css';
import SupplierCreditAllocationModal from './SupplierCreditAllocationModal';
import { DuplicateBillsNotice } from './DuplicateBillsReview';
import { allocateRowBlock, allocateLabel, addCreditRowBlock, startSupplierCredit, isSupplierCredit } from '../utils/supplierCredits';

// The most rows an export will fetch for "all results" — the export route's own cap.
const EXPORT_ALL_LIMIT = 1000;

/**
 * Columns filterable from their header.
 *
 * Module-level for a stable object identity, and the keys must match the
 * whitelist in billService.searchBills — an unknown key narrows nothing rather
 * than erroring, so a mismatch shows up as a filter that quietly does nothing.
 */
const COLUMN_FILTERS = {
  supplier: { label: 'Supplier' },
  /* Queried is a thing that is true OF a bill, not a fourth value of its
     status, so it sits beside it rather than in it. */
  status: {
    label: 'Status',
    sections: [
      { key: 'status', label: 'Bill status' },
      { key: 'is_queried', label: 'Queried' }
    ]
  },
  /* The Due Date header asks the question people have of a due date. Faceting
     the dates themselves offers several hundred values nobody can use. */
  due_state: { label: 'Due' },
  type: { label: 'Type' }
};

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
/** The Status filter's words for a stored status (module-level: a stable identity). */
const STATUS_OPTION_LABEL = (key, value) => (key === 'status' ? billStatusLabel(value) : undefined);

/** Why the Stock toggle and bulk change are greyed: their migration has not run. */
const NOT_READY = 'Stock is not switched on yet — the database update for it has not been run.';

const CLEARED_FILTERS = {
  search: '', status: 'all', datePreset: 'all-time', archived: false, queried: false, stock: false,
  transaction_type: 'all', dateFrom: '', dateTo: '',
  page: 1, limit: '20', includeAdditives: true
};

function SearchBills({ user, onNavigate }) {
  /*
   * Arriving from a modal's "Find in …" (utils/revealInSearch.js): the
   * reference seeds the filters and the page goes back to 1, both in the FIRST
   * render's state, so the mount search is the one that was asked for. Read
   * before the usePersistedState calls below, which it seeds.
   */
  const reveal = useReveal('search-bills');

  const { openEntity } = useEntityModal();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  // PO Reference / Stock usable (their migration has run) — from the search.
  const [localFieldsReady, setLocalFieldsReady] = useState(true);
  const [selectedBillId, setSelectedBillId] = usePersistedState('searchBills.billModalId', null);
  const [selectedPOId, setSelectedPOId] = usePersistedState('searchBills.poModalId', null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [showSyncWarning, setShowSyncWarning] = useState(false);
  const [pendingSyncAction, setPendingSyncAction] = useState(null);


  // Expandable rows
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [lineItems, setLineItems] = useState({});
  const [loadingLineItems, setLoadingLineItems] = useState({});
  const [expandingAll, setExpandingAll] = useState(false);
  // Distinct values per filterable column across the whole result set.
  const [facets, setFacets] = useState({});
  // Expand All is a mode that survives pagination and re-searching, not a
  // one-off action on the rows that happen to be on screen.
  const [expandAllMode, setExpandAllMode] = usePersistedState('searchBills.expandAll', false);
  // The "Delivered, not billed" panel above the list.
  const [showDelivered, setShowDelivered] = usePersistedState('searchBills.showDelivered', false);

  // Bulk selection
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // "Dated in a month that should be locked" — the bulk status change asks once.
  const priorMonth = usePriorMonthConfirm();
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);
  const [bulkActionsClosing, setBulkActionsClosing] = useState(false);

  // Slideout buttons - re-measure when bulk actions bar appears
  useSlideOutButton([bulkActionsVisible]);

  // Drag-to-resize columns (12 cols: checkbox, expand, reference, supplier,
  // PO number, bill date, due date, total, paid, balance, status, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('searchBills', 12);

  const [pagination, setPagination] = usePersistedState('searchBills.pagination', { page: 1, limit: 20, total: 0, totalPages: 0 },
    { seed: reveal && { page: 1 } });
  const [filters, setFilters] = usePersistedState('searchBills.filters', {
    search: '',
    status: 'all',
    datePreset: 'all-time',
    archived: false,
    queried: false,
    stock: false,
    transaction_type: 'all',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: '20',
    includeAdditives: true
    // merge: a filter added later (Queried) starts from its default rather
    // than undefined in a tab that stored the filters before it existed.
  }, { merge: true, seed: revealFilters(reveal) });
  const [sortField, setSortField] = usePersistedState('searchBills.sortField', 'bill_date');
  const [sortDirection, setSortDirection] = usePersistedState('searchBills.sortDirection', 'desc');

  // Right-click menu. selectedBills stores bill.id, so getRowId must match.
  // Right-click a header to narrow by the values on this page. Only the columns
  // whose values repeat — a filter on a reference or a total is a list where
  // every option matches one row.
  const cols = useColumnFilters(bills, COLUMN_FILTERS, {
    facets,
    storageKey: 'searchBills.columnFilters',
    onChange: () => setPagination(prev => ({ ...prev, page: 1 })),
    // Authorised reads "Approved" in the Status list; the value is unchanged.
    optionLabel: STATUS_OPTION_LABEL
  });

  // How much of the table is still off to the right, for the cue.
  const [tableScrollRef, tableScroll] = useScrollAffordance([]);

  const ctxMenu = useTableContextMenu({
    rows: bills,
    selectedIds: selectedBills,
    getRowId: (bill) => bill.id
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    // Back to the page you were on, not page 1: the filters are restored from
    // the nav store, so the page number should be too. (Arriving with a
    // search handed over, both were seeded above — page 1.)
    searchBills(pagination.page || 1);
    setTimeout(() => { isInitialMount.current = false; }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) return;
    const id = setTimeout(() => searchBills(1), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A column filter is part of the query, so changing one re-runs the search
  // the same way changing a filter above the table does.
  useEffect(() => {
    if (isInitialMount.current) return;
    searchBills(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.queryValue]);

  useEffect(() => {
    if (isInitialMount.current) return;
    searchBills(pagination.page || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  // Clear selection when results change
  useEffect(() => { setSelectedBills(new Set()); }, [bills]);

  // Bulk bar animation
  useEffect(() => {
    if (selectedBills.size > 0) {
      if (!bulkActionsVisible || bulkActionsClosing) {
        setBulkActionsVisible(true);
        setBulkActionsClosing(false);
      }
    } else if (selectedBills.size === 0 && bulkActionsVisible && !bulkActionsClosing) {
      setBulkActionsClosing(true);
      // No clearTimeout cleanup — setBulkActionsClosing(true) re-runs this
      // effect, and cancelling the timer would leave the bar stuck visible.
      setTimeout(() => { setBulkActionsVisible(false); setBulkActionsClosing(false); }, 250);
    }
  }, [selectedBills.size, bulkActionsVisible, bulkActionsClosing]);

  // The search's query string for a page of results — shared by the screen and
  // the export's "all results", so the two cannot filter differently.
  const searchParamsFor = (page, limit, searchOverride) => {
      const cleanFilters = {
        // 'all'             → empty status, backend applies default exclusion of Deleted/Voided
        // 'all_inc_deleted' → sentinel, backend skips the default exclusion
        // anything else     → exact status match
        search: searchOverride !== undefined ? searchOverride : (filters.search || ''),
        status: filters.status === 'all' ? '' : filters.status,
        archived: filters.archived ? 'true' : 'false',
        // Queried only when the toggle is on; off narrows nothing.
        is_queried: filters.queried ? 'true' : '',
        // Stock bills only when the toggle is on; off narrows nothing.
        stock: filters.stock ? 'true' : '',
        transaction_type: filters.transaction_type === 'all' ? '' : filters.transaction_type,
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        limit: limit || filters.limit || 20,
        includeAdditives: filters.includeAdditives ? 'true' : 'false'
      };
      const queryParams = new URLSearchParams({ ...cleanFilters, page, sortField, sortDirection });
      // Column-header filters travel with the search, so they narrow the whole
      // result set and survive paginating and re-searching.
      if (cols.queryValue) queryParams.set('columnFilters', cols.queryValue);
      return queryParams;
  };

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const searchBills = async (page = 1, searchOverride) => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const queryParams = searchParamsFor(page, null, searchOverride);
      const response = await fetch(`${API_URL}/api/bills/search?${queryParams}`, { credentials: 'include' });
      const data = await response.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      if (data.success) {
        setBills(data.bills || []);
        if (data.facets) setFacets(data.facets);
        setLocalFieldsReady(data.local_fields_ready !== false);
        setPagination({
          page: data.currentPage || page,
          limit: parseInt(filters.limit),
          // totalCount survives cost redaction; `total` does not.
          total: data.totalCount ?? data.total ?? 0,
          totalPages: data.totalPages || 0
        });
      }
    } catch (error) {
      if (!latest.isLatest(seq)) return;
      setAlertDialog({ isOpen: true, message: 'Failed to search bills: ' + error.message, type: 'error' });
    }
    if (latest.isLatest(seq)) setLoading(false);
  };

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSortField('bill_date');
    setSortDirection('desc');
    // Column filters are filters; leaving them on after "clear all filters" is
    // how a search gets reported as broken.
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, { labels: { transaction_type: 'Type', queried: 'Queried', stock: 'Stock' } });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;
  const handleSort = (field) => {
    if (field === sortField) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };
  const changePage = (p) => { if (p >= 1 && p <= pagination.totalPages) searchBills(p); };

  const linkPOsFromHistory = async () => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Calculating...');
    const eventSource = new EventSource(`${API_URL}/api/bills/link-pos`, { withCredentials: true });
    eventSource.onmessage = (event) => {
      const d = JSON.parse(event.data);
      if (d.type === 'progress') {
        setSyncProgress(d.percentage || 0);
        setSyncMessage(d.status);
      } else if (d.type === 'complete') {
        setSyncProgress(100);
        setSyncMessage('PO linking complete!');
        setAlertDialog({ isOpen: true, message: d.result.message, type: 'success' });
        searchBills(1);
        eventSource.close();
        setTimeout(() => { setLoading(false); setSyncProgress(0); setSyncMessage(''); }, 1500);
      } else if (d.type === 'error') {
        setAlertDialog({ isOpen: true, message: `PO linking failed: ${d.error}`, type: 'error' });
        eventSource.close();
        setLoading(false); setSyncProgress(0); setSyncMessage('');
      }
    };
    eventSource.onerror = () => {
      setAlertDialog({ isOpen: true, message: 'Connection lost during PO linking', type: 'error' });
      eventSource.close();
      setLoading(false); setSyncProgress(0); setSyncMessage('');
    };
  };

  const openSyncModal = () => {
    setSyncDateFrom(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setSyncDateTo(new Date().toISOString().split('T')[0]);
    setShowSyncModal(true);
  };

  const syncBills = async (syncType = 'custom') => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Initiating sync...');
    setShowSyncModal(false);

    const today = new Date().toISOString().split('T')[0];
    let startDate, endDate;

    if (syncType === '3months') {
      startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      endDate = today;
    } else if (syncType === 'full') {
      startDate = '2020-01-01';
      endDate = today;
    } else {
      startDate = syncDateFrom || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      endDate = syncDateTo || today;
    }

    const eventSource = new EventSource(
      `${API_URL}/api/bills/sync?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      { withCredentials: true }
    );
    eventSource.onmessage = (event) => {
      const d = JSON.parse(event.data);
      if (d.type === 'progress') {
        setSyncProgress(d.percentage != null ? d.percentage : Math.min(95, Math.round((d.currentPage * 100) / Math.max(d.currentPage + 1, 10))));
        setSyncMessage(d.message || d.status || `Fetching page ${d.currentPage}... (${d.recordsFetched} records)`);
      } else if (d.type === 'complete') {
        setSyncProgress(100);
        setSyncMessage('Sync complete!');
        setAlertDialog({ isOpen: true, message: d.result.message || `Synced ${d.result.total} bills`, type: 'success' });
        searchBills(1);
        eventSource.close();
        setTimeout(() => { setLoading(false); setSyncProgress(0); setSyncMessage(''); }, 1500);
      } else if (d.type === 'error') {
        setAlertDialog({ isOpen: true, message: `Sync failed: ${d.error}`, type: 'error' });
        eventSource.close();
        setLoading(false); setSyncProgress(0); setSyncMessage('');
      }
    };
    eventSource.onerror = () => {
      setAlertDialog({ isOpen: true, message: 'Connection to server lost during sync', type: 'error' });
      eventSource.close();
      setLoading(false); setSyncProgress(0); setSyncMessage('');
    };
  };

  // Row expand
  const toggleRowExpand = async (billId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(billId)) {
      newExpanded.delete(billId);
    } else {
      newExpanded.add(billId);
      if (!lineItems[billId]) await fetchLineItems(billId);
    }
    setExpandedRows(newExpanded);
  };

  const fetchLineItems = async (billId) => {
    setLoadingLineItems(prev => ({ ...prev, [billId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/bills/${billId}/lines`, { credentials: 'include' });
      const data = await response.json();
      setLineItems(prev => ({ ...prev, [billId]: data.lines || [] }));
    } catch { setLineItems(prev => ({ ...prev, [billId]: [] })); }
    finally { setLoadingLineItems(prev => ({ ...prev, [billId]: false })); }
  };

  /**
   * The rows on screen, with each bill's lines beneath it — the expanded view
   * on paper. Lines are fetched here rather than read off the expanded-row
   * cache, which only holds what has been opened.
   */
  const buildExport = async ({ scope = 'page' } = {}) => {
    // "All results" fetches past the page on screen with the same filters and
    // sort, up to the export's own row limit.
    const onPage = cols.rows;
    const totalAvailable = Math.max(Number(pagination.total) || 0, onPage.length);
    let source = onPage;
    if (scope === 'all' && totalAvailable > onPage.length) {
      try {
        const res = await fetch(`${API_URL}/api/bills/search?${searchParamsFor(1, EXPORT_ALL_LIMIT)}`,
          { credentials: 'include' });
        const data = await res.json();
        if (Array.isArray(data.bills)) source = data.bills;
      } catch (error) {
        console.error('Could not fetch every result for the export:', error);
      }
    }

    let lines = { ...lineItems };
    const missing = source.map((b) => b.id).filter((id) => !lines[id]);
    if (missing.length) {
      try {
        const response = await fetch(`${API_URL}/api/bills/lines/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids: missing })
        });
        const data = await response.json();
        lines = { ...lines, ...(data.lines || {}) };
        setLineItems((prev) => ({ ...prev, ...(data.lines || {}) }));
      } catch (error) {
        // An export without line items beats no export at all.
        console.error('Could not load bill lines for the export:', error);
      }
    }

    const detailColumns = [
      { key: 'code', label: 'Item #', width: '15%' },
      { key: 'description', label: 'Purchase description', width: '40%' },
      { key: 'qty', label: 'Qty', width: '9%', numeric: true },
      { key: 'unit', label: 'Unit', width: '12%', numeric: true },
      { key: 'lineTotal', label: 'Line', width: '12%', numeric: true },
      { key: 'account', label: 'Account', width: '12%' }
    ];

    const criteria = [];
    if (filters.search) criteria.push({ label: 'Search', value: filters.search });
    if (filters.status && filters.status !== 'all') criteria.push({ label: 'Status', value: billStatusLabel(filters.status) });
    if (filters.type && filters.type !== 'all') criteria.push({ label: 'Type', value: filters.type });
    if (filters.stock) criteria.push({ label: 'Stock', value: 'Stock bills only' });
    if (filters.dateFrom || filters.dateTo) {
      criteria.push({ label: 'Dated', value: `${filters.dateFrom || 'any'} to ${filters.dateTo || 'any'}` });
    }
    for (const [key, values] of Object.entries(cols.selected)) {
      if (!Array.isArray(values)) continue;
      const shown = key === 'status' ? values.map(billStatusLabel) : values;
      criteria.push({ label: cols.definitions[key]?.label || key, value: shown.join(', ') || 'none' });
    }

    return {
      title: 'Supplier Bills',
      kicker: 'BILLS',
      rowNoun: source.length === 1 ? 'bill' : 'bills',
      filename: `bills-${new Date().toISOString().slice(0, 10)}`,
      landscape: true,
      columns: [
        { key: 'reference', label: 'Reference', width: '13%' },
        { key: 'supplier', label: 'Supplier', width: '22%' },
        { key: 'po', label: 'PO Number', width: '13%' },
        { key: 'billDate', label: 'Bill Date', width: '9%' },
        { key: 'dueDate', label: 'Due Date', width: '9%' },
        { key: 'total', label: 'Total', width: '9%', numeric: true },
        { key: 'paid', label: 'Paid', width: '9%', numeric: true },
        { key: 'balance', label: 'Balance', width: '9%', numeric: true },
        { key: 'status', label: 'Status', width: '10%' }
      ],
      rows: source.map((bill) => ({
        reference: bill.supplier_reference || bill.bill_number || '',
        supplier: bill.supplier_name || bill.account_company_name || '',
        po: [...billPoLabels(bill), ...(bill.po_reference ? [`Ref ${bill.po_reference}`] : [])].join(', '),
        billDate: formatDate(bill.bill_date),
        dueDate: formatDate(bill.due_date),
        total: formatCurrency(bill.total),
        paid: formatCurrency(bill.amount_paid),
        // Computed the same way the table computes it — there is no balance
        // column on purchasing.bills, and inventing one here would let the
        // printout disagree with the screen.
        balance: formatCurrency((Number(bill.total) || 0) - (Number(bill.amount_paid) || 0)),
        status: billStatusLabel(bill.status),
        detail: {
          columns: detailColumns,
          emptyLabel: 'No lines on this bill',
          rows: (lines[bill.id] || []).map((line) => ({
            code: line.item_code || '',
            description: line.description || '',
            // Its own unit and the tonnes it comes to (utils/qtyWithTonnes).
            qty: quantityWithTonnesText(line.quantity, qtyFactsOf(line.qty_facts || {})),
            unit: line.unit_amount !== undefined && line.unit_amount !== null
              ? formatPrice(line.unit_amount) : '',
            lineTotal: line.line_amount !== undefined && line.line_amount !== null
              ? formatCurrency(line.line_amount) : '',
            account: line.account_code || ''
          }))
        }
      })),
      totals: [{
        reference: `${source.length} bills`,
        total: formatCurrency(source.reduce((sum, b) => sum + (Number(b.total) || 0), 0)),
        paid: formatCurrency(source.reduce((sum, b) => sum + (Number(b.amount_paid) || 0), 0)),
        balance: formatCurrency(source.reduce(
          (sum, b) => sum + ((Number(b.total) || 0) - (Number(b.amount_paid) || 0)), 0))
      }],
      criteria,
      available: { page: onPage.length, all: totalAvailable },
      scopeNote: scope === 'all' && totalAvailable > EXPORT_ALL_LIMIT
        ? `First ${EXPORT_ALL_LIMIT.toLocaleString('en-GB')} of ${totalAvailable.toLocaleString('en-GB')} results`
        : ''
    };
  };

  const handleExpandAll = async () => {
    if (expandAllMode) { setExpandAllMode(false); setExpandedRows(new Set()); return; }
    setExpandAllMode(true);
    await expandEveryRow(bills);
  };

  /** Open every row, fetching what is missing in ONE request rather than N. */
  const expandEveryRow = useCallback(async (rows) => {
    if (!rows.length) { setExpandedRows(new Set()); return; }
    const allIds = new Set(rows.map(b => b.id));
    const idsToFetch = rows.filter(b => !lineItems[b.id]).map(b => b.id);
    if (idsToFetch.length > 0) {
      setExpandingAll(true);
      const loadingState = {};
      idsToFetch.forEach(id => { loadingState[id] = true; });
      setLoadingLineItems(prev => ({ ...prev, ...loadingState }));
      try {
        const response = await fetch(`${API_URL}/api/bills/lines/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids: idsToFetch })
        });
        const data = await response.json();
        setLineItems(prev => ({ ...prev, ...(data.lines || {}) }));
      } catch (error) {
        console.error('Failed to fetch bill lines:', error);
      } finally {
        const done = {};
        idsToFetch.forEach(id => { done[id] = false; });
        setLoadingLineItems(prev => ({ ...prev, ...done }));
        setExpandingAll(false);
      }
    }
    setExpandedRows(allIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  // Re-apply the mode when the results change, so paginating or searching again
  // does not silently return the table to collapsed under a "Collapse All"
  // button.
  useEffect(() => {
    if (!expandAllMode || !bills.length) return;
    expandEveryRow(bills);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills, expandAllMode]);

  /**
   * Archive or restore one bill. The list re-reads afterwards: an archived
   * bill leaves the active view, so the row should not still be sitting there.
   */
  const handleArchiveBill = async (bill, archived) => {
    try {
      const res = await fetch(`${API_URL}/api/bills/${bill.id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ archived })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not ${archived ? 'archive' : 'restore'} the bill.`);
      setAlertDialog({
        isOpen: true,
        type: 'success',
        message: `${bill.supplier_reference || bill.bill_number} ${archived ? 'archived' : 'restored'}.`
      });
      searchBills(pagination.page);
    } catch (error) {
      setAlertDialog({ isOpen: true, type: 'error', message: error.message });
    }
  };

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedBills(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  const toggleSelectBill = (id) => {
    setSelectedBills(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const toggleSelectAll = () => {
    if (selectedBills.size === bills.length) setSelectedBills(new Set());
    else setSelectedBills(new Set(bills.map(b => b.id)));
  };

  const handleBulkDelete = () => {
    const nonDeletable = bills.filter(b => selectedBills.has(b.id) && b.source === 'xero' && b.status !== 'Draft');
    if (nonDeletable.length > 0) {
      setAlertDialog({ isOpen: true, message: `${nonDeletable.length} selected bill(s) cannot be deleted (Xero bills must be in Draft status).`, type: 'warning' });
      return;
    }
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Bills',
      message: `Delete ${selectedBills.size} bill${selectedBills.size !== 1 ? 's' : ''}? This cannot be undone.`,
      onConfirm: confirmBulkDelete
    });
  };

  const confirmBulkDelete = async () => {
    setBulkActionLoading(true);
    let deleted = 0;
    let failed = 0;
    // Why each refusal happened (a bill in Xero is taken out of Xero first, and
    // Xero may say no — paid, a locked period, not connected).
    const reasons = new Set();
    for (const id of selectedBills) {
      try {
        const r = await fetch(`${API_URL}/api/bills/${id}`, { method: 'DELETE', credentials: 'include' });
        const d = await r.json();
        if (d.success) deleted++; else { failed++; if (d.error) reasons.add(d.error); }
      } catch { failed++; }
    }
    const why = reasons.size ? ` ${[...reasons].slice(0, 3).join(' ')}` : '';
    setAlertDialog({ isOpen: true, message: `Deleted ${deleted} bill(s)${failed > 0 ? `. ${failed} failed.${why}` : ''}`, type: failed > 0 ? (deleted > 0 ? 'warning' : 'error') : 'success' });
    setSelectedBills(new Set());
    searchBills(pagination.page);
    setBulkActionLoading(false);
  };

  // Mark as Stock / Clear Stock on the ticked bills — local only (2026-10-14).
  const handleBulkStock = async (isStock) => {
    if (!selectedBills.size) return;
    setBulkActionLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/bills/stock`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selectedBills], is_stock: isStock })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || 'The bills could not be changed');
      const n = (d.changed || []).length;
      const same = (d.unchanged || []).length;
      setAlertDialog({
        isOpen: true, type: 'success',
        message: `${n} bill${n === 1 ? '' : 's'} ${isStock ? 'marked as Stock' : 'no longer Stock'}`
          + `${same ? ` (${same} already ${isStock ? 'Stock' : 'not Stock'})` : ''}.`
      });
      setSelectedBills(new Set());
      searchBills(pagination.page || 1);
    } catch (err) {
      setAlertDialog({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (!newStatus || !selectedBills.size) return;
    setBulkActionLoading(true);
    let updated = 0;
    let failed = 0;
    let wentBack = 0;
    const send = (id, extra = {}) => fetch(`${API_URL}/api/bills/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, ...extra })
    }).then(r => r.json());
    // Bills dated in a month that should be locked in Xero by now are asked
    // about together, once, after the rest (Cam, 15 Oct 2026).
    const asked = [];
    for (const id of selectedBills) {
      try {
        const d = await send(id);
        if (d.success) updated++;
        else if (isPriorMonthConfirm(d)) asked.push({ id, d });
        else failed++;
      } catch { failed++; }
    }
    if (asked.length) {
      const lockDate = asked[0].d.prior_month?.lock_date || null;
      const question = priorMonthQuestionForMany(asked.map(({ id, d }) => {
        const row = bills.find(b => String(b.id) === String(id));
        const ref = row?.supplier_reference || row?.bill_number || `#${id}`;
        return { what: `Bill ${ref}`, dates: d.prior_month?.dates || [] };
      }), { effectiveLockDate: lockDate });
      if (await priorMonth.ask(question || asked[0].d)) {
        for (const { id } of asked) {
          try {
            const d = await send(id, PRIOR_MONTH_CONFIRMED);
            if (d.success) updated++; else failed++;
          } catch { failed++; }
        }
      } else {
        wentBack = asked.length;
      }
    }
    setAlertDialog({
      isOpen: true,
      message: `Updated ${updated} bill(s) to ${billStatusLabel(newStatus)}${failed > 0 ? `. ${failed} failed.` : ''}`
        + (wentBack ? ` ${wentBack} dated in a month that should be locked in Xero ${wentBack === 1 ? 'was' : 'were'} left as ${wentBack === 1 ? 'it was' : 'they were'}.` : ''),
      type: updated > 0 ? 'success' : (wentBack && !failed ? 'info' : 'error')
    });
    setSelectedBills(new Set());
    searchBills(pagination.page);
    setBulkActionLoading(false);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
  };

  const getStatusBadgeClass = (status) => {
    const map = { DRAFT: 'draft', Draft: 'draft', SUBMITTED: 'submitted', Submitted: 'submitted', AUTHORISED: 'authorised', Authorised: 'authorised', PAID: 'billed', Paid: 'billed', VOIDED: 'deleted', Voided: 'deleted', DELETED: 'deleted', Deleted: 'deleted' };
    return `status-badge ${map[status] || 'default'}`;
  };

  // Which edit a row's bill gets (utils/billEdit — the server's rule): with
  // editing bills in Xero switched on, a bill in Xero opens for editing and
  // only what is changed is sent there. The server decides again on save.
  const [editSettings, setEditSettings] = useState({ switchOn: false, canEditPosted: false });
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/bills/push-settings`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!cancelled && d.success) setEditSettings({ switchOn: !!d.edit_in_xero_enabled, canEditPosted: !!d.can_edit_posted });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const canEdit = (bill) => billEditMode(bill, editSettings).editable;
  const editTitle = (bill) => {
    const m = billEditMode(bill, editSettings);
    if (m.editable) return m.mode === 'patch' ? 'Edit — only what you change is sent to Xero' : 'Edit';
    return `Cannot edit — ${m.block || 'not here'}`;
  };

  // Shared by the row actions menu and the right-click context menu so the two
  // surfaces can never drift apart.
  // Print, email and download — one modal for one bill or a selection. A bill
  // copy is ours: the modal offers only Excel Agri addresses to send it to.
  const [documentOutput, setDocumentOutput] = useState(null); // { mode, ids }
  // The bill whose status / Queried is being changed (BillStatusDialog).
  const [statusBill, setStatusBill] = useState(null);
  // The bill whose cost is being put onto its POs (UpdatePoFromBillDialog).
  const [poCostBillId, setPoCostBillId] = useState(null);
  // A voided bill someone tried to edit or change (VoidedDocumentDialog).
  const [voidedBillId, setVoidedBillId] = useState(null);
  // The credit (or bill) being allocated (SupplierCreditAllocationModal).
  const [allocDocId, setAllocDocId] = useState(null);
  const openDocuments = (mode, ids) => {
    const list = Array.from(ids || []).filter(Boolean);
    if (list.length === 0) return;
    setDocumentOutput({ mode, ids: list });
  };

  const buildRowActions = (bill) => [
    // Opening the bill here replaces the old "View in Xero" jump: the record is
    // in this system, and a link out of it was a link away from everything else
    // a user could do with it.
    { key: 'view', readSafe: true, icon: 'visibility', label: 'View', title: 'View',
      onClick: () => openEntity('bill', bill.id) },
    {
      key: 'edit',
      icon: 'edit',
      label: 'Edit',
      // A voided bill: the button stays live and explains (raise a copy, or
      // email accounts) — it cannot be edited or restored anywhere.
      disabled: !canEdit(bill) && !isVoided(bill.status),
      title: isVoided(bill.status)
        ? 'Voided — it cannot be edited or restored. Raise a copy, or email accounts'
        : editTitle(bill),
      onClick: () => {
        if (isVoided(bill.status)) { setVoidedBillId(bill.id); return; }
        if (canEdit(bill)) { sessionStorage.setItem('editBillId', bill.id); onNavigate && onNavigate('create-bill'); }
      },
    },
    {
      // Status and Queried (BillStatusDialog). On a bill entered here that is
      // in Xero this is the one change that reaches Xero; every row carries
      // it, disabled with the reason where it cannot apply.
      key: 'status',
      icon: 'rule',
      label: 'Status',
      disabled: !statusChangeAllowed(bill).ok && !isVoided(bill.status),
      title: isVoided(bill.status)
        ? 'Voided — its status cannot change. Raise a copy, or email accounts'
        : statusChangeAllowed(bill).ok ? 'Change status / Queried' : `Cannot change the status — ${statusChangeAllowed(bill).reason}`,
      onClick: () => {
        if (isVoided(bill.status)) { setVoidedBillId(bill.id); return; }
        if (statusChangeAllowed(bill).ok) setStatusBill(bill);
      },
    },
    {
      // The bill's cost onto its POs — the user's choice (Cam, 2026-10-08).
      key: 'update-po',
      icon: 'price_check',
      label: 'Update PO from this bill…',
      disabled: !!poCostRowBlock(bill),
      title: poCostRowBlock(bill) || 'Choose which PO lines take this bill\'s cost',
      onClick: () => { if (!poCostRowBlock(bill)) setPoCostBillId(bill.id); },
    },
    {
      // A supplier credit against this bill: Add Supplier Credit with the
      // supplier and this bill filled in (2026-10-08).
      key: 'add-credit',
      icon: 'note_add',
      label: 'Add credit against this bill',
      disabled: !!addCreditRowBlock(bill),
      title: addCreditRowBlock(bill) || 'Add a supplier credit against this bill',
      onClick: () => {
        if (addCreditRowBlock(bill)) return;
        startSupplierCredit(onNavigate, {
          account_id: bill.account_id, xero_contact_id: bill.xero_contact_id, supplier_name: bill.supplier_name || bill.account_company_name,
          bill: { id: bill.id, supplier_reference: bill.supplier_reference || bill.bill_number }
        });
      },
    },
    {
      // Allocate a credit to bills / apply a credit to a bill — in Xero first.
      key: 'allocate',
      icon: 'credit_score',
      label: allocateLabel(bill),
      disabled: !!allocateRowBlock(bill),
      title: allocateRowBlock(bill) || (isSupplierCredit(bill) ? 'Allocate this credit to the supplier\'s bills' : 'Apply one of the supplier\'s credits to this bill'),
      onClick: () => { if (!allocateRowBlock(bill)) setAllocDocId(bill.id); },
    },
    {
      /*
       * Archive, where Delete used to be. Delete only ever worked on drafts
       * and local bills, so the thing most people actually wanted — get this
       * out of my list — had no button at all. Archiving has no such
       * condition: a paid bill from two years ago is exactly what you archive.
       */
      key: 'archive',
      icon: bill.is_archived ? 'unarchive' : 'archive',
      label: bill.is_archived ? 'Restore' : 'Archive',
      title: bill.is_archived
        ? 'Put this bill back in the active list'
        : 'Move this bill out of the active list. Nothing is lost.',
      onClick: () => handleArchiveBill(bill, !bill.is_archived),
    },
    { key: 'export', icon: 'ios_share', label: 'Export', title: 'Export — preview, print, download or email to yourself', onClick: () => openDocuments('download', [bill.id]) },
  ];

  return (
    <div className="search-bills">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search, add, and manage bills.</span>
          <span className="note">
              <span className="material-symbols-rounded">info</span>
              <span style={{ margin: 'auto 0' }}>
                Xero-source bills are view-only; create/ edit/ archive for local-source bills will sync with Xero.
                <LastXeroSync entity="bills" />
              </span>
            </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <ExportTableButton
                build={buildExport}
                user={user}
                rowCount={cols.rows.length}
                onError={(message) => setAlertDialog({ isOpen: true, message, type: 'error' })}
              />
            </div>
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {user?.is_admin && (
              <>
                <div>
                  <button onClick={() => { setPendingSyncAction(() => () => syncBills('3months')); setShowSyncWarning(true); }} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                    <span className="btn-label">Sync 3 Months</span>
                  </button>
                </div>
                <div>
                  <button onClick={openSyncModal} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">date_range</span></div>
                    <span className="btn-label">Custom Dates</span>
                  </button>
                </div>
                <div>
                  <button onClick={() => { setPendingSyncAction(() => () => syncBills('full')); setShowSyncWarning(true); }} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_download</span></div>
                    <span className="btn-label">Full Sync</span>
                  </button>
                </div>
                <div>
                  <button onClick={linkPOsFromHistory} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">link</span></div>
                    <span className="btn-label">Link POs</span>
                  </button>
                </div>
              </>
            )}
            <div>
              <button onClick={() => startSupplierCredit(onNavigate)} className="btn-slideout" disabled={loading} data-write-action
                title="Enter a supplier's credit note — sent to Xero like a bill">
                <div className="icon-wrapper"><span className="material-symbols-rounded">note_add</span></div>
                <span className="btn-label">Add Supplier Credit</span>
              </button>
            </div>
            <div>
              <button onClick={() => onNavigate && onNavigate('bulk-add-bills')} className="btn-slideout" disabled={loading} data-write-action
                title="Upload many supplier invoices at once (or a PDF with several bills in it), then accept or review each">
                <div className="icon-wrapper"><span className="material-symbols-rounded">library_add</span></div>
                <span className="btn-label">Bulk Add Bills</span>
              </button>
            </div>
            <div>
              <button onClick={() => onNavigate && onNavigate('create-bill')} className="btn-slideout" disabled={loading}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">Add Bill</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row bills-filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp
                amounts
                fields={['bill no.', 'supplier', 'reference', 'supplier ref.', 'PO reference', 'account no.', 'PO no.', 'product', 'item code', 'additives', 'total']}
              />
            </label>
            <div className="search-with-additives">
              <Input type="text" placeholder='Search bills...' value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onEnter={() => searchBills(1)} icon="search" />
              <TickCheckbox
                id="bill-include-additives"
                checked={filters.includeAdditives}
                onChange={(e) => handleFilterChange('includeAdditives', e.target.checked)}
                label="+ADD"
                className="include-additives-pill"
                ariaLabel="Include additives"
              />
            </div>
          </div>
          {/* Status and Queried have gone from the row: both are sections
              under the Status header now, where they narrow the whole result
              set rather than holding one value each. */}
          <div className="filter-group">
            <label>Type</label>
            <Dropdown value={filters.transaction_type} onChange={(v) => handleFilterChange('transaction_type', v)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'BILL', label: 'Bill' },
                { value: 'CREDIT', label: 'Credit Note' },
                { value: 'OVERPAYMENT', label: 'Overpayment' },
                { value: 'PREPAYMENT', label: 'Prepayment' }
              ]} placeholder="All Types" />
          </div>
          <DateRangeFilter
            from={filters.dateFrom}
            to={filters.dateTo}
            preset={filters.datePreset}
            onChange={({ from, to, preset }) => setFilters(prev => ({
              ...prev, dateFrom: from, dateTo: to, datePreset: preset, page: 1
            }))}
          />
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown value={filters.limit} onChange={(v) => handleFilterChange('limit', v)}
              options={[{ value: '20', label: '20' }, { value: '50', label: '50' }, { value: '100', label: '100' }, { value: '200', label: '200' }]}
              placeholder="20" />
          </div>
          {/* Overdue moved to the Due Date header, where it sits beside the
              other answers to the same question. This slot carries Archived
              instead — one or the other, never mixed. */}
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="bills-archived"
                  type="checkbox"
                  checked={!!filters.archived}
                  onChange={(e) => handleFilterChange('archived', e.target.checked)}
                />
                <label htmlFor="bills-archived"></label>
              </div>
            </div>
          </div>
          {/* Queried bills only — beside Archived, the same kind of question. */}
          <div className="filter-group">
            <label>Queried</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="bills-queried"
                  type="checkbox"
                  checked={!!filters.queried}
                  onChange={(e) => handleFilterChange('queried', e.target.checked)}
                />
                <label htmlFor="bills-queried"></label>
              </div>
            </div>
          </div>
          {/* Stock bills only (2026-10-14) — goods bought for our own stock. */}
          <div className="filter-group">
            <label title={localFieldsReady ? 'Only bills marked Stock' : NOT_READY}>Stock</label>
            <div className="checkbox-container" title={localFieldsReady ? 'Only bills marked Stock' : NOT_READY}>
              <div className="check light">
                <input
                  id="bills-stock"
                  type="checkbox"
                  checked={!!filters.stock}
                  disabled={!localFieldsReady}
                  onChange={(e) => handleFilterChange('stock', e.target.checked)}
                />
                <label htmlFor="bills-stock"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {syncMessage && (
        <div className="loading-bar-wrapper">
          <LoadingBar
            progress={syncProgress}
            message={syncMessage}
            indeterminate={syncProgress === 0}
          />
        </div>
      )}

      {bulkActionsVisible && (
        <div className={`bulk-actions-bar ${bulkActionsClosing ? 'closing' : ''}`}>
          <div className="bulk-actions-info">
            <span className="bulk-actions-title">Bulk Actions:</span>
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input type="checkbox" className="tick-check-input" id="bulk-bills-indicator" checked readOnly />
              <label htmlFor="bulk-bills-indicator" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z" />
                  <polyline points="1 9 7 14 15 4" />
                </svg>
              </label>
            </label>
            <span>{selectedBills.size} item{selectedBills.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <Dropdown
              value=""
              onChange={(value) => value && handleBulkStatusChange(value)}
              options={[
                { value: '', label: 'Change Status...' },
                { value: 'Draft', label: 'Draft' },
                { value: 'Authorised', label: billStatusLabel('Authorised') }
              ]}
              placeholder="Change Status..."
              disabled={bulkActionLoading}
            />
            {/* This system's own, never sent to Xero (2026-10-14). */}
            <div title={localFieldsReady ? 'Mark the selected bills as Stock, or clear it' : NOT_READY}>
              <Dropdown
                value=""
                onChange={(value) => value && handleBulkStock(value === 'mark')}
                options={[
                  { value: '', label: 'Stock...' },
                  { value: 'mark', label: 'Mark as Stock' },
                  { value: 'clear', label: 'Clear Stock' }
                ]}
                placeholder="Stock..."
                disabled={bulkActionLoading || !localFieldsReady}
              />
            </div>
            <div>
              <button className="btn-slideout btn-danger" onClick={handleBulkDelete} disabled={bulkActionLoading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">delete</span>
                </div>
                <span className="btn-label">Delete Selected</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout" onClick={() => setSelectedBills(new Set())} disabled={bulkActionLoading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bills-panel">
        <div className="table-toolbar">
          <button className="btn-secondary btn-sm" onClick={handleExpandAll}
            disabled={loading || expandingAll || bills.length === 0}>
            <span className="material-symbols-rounded">
              {expandAllMode ? 'unfold_less' : 'unfold_more'}
            </span>
            {expandingAll ? 'Loading...' : (expandAllMode ? 'Collapse All' : 'Expand All')}
          </button>

          <button className={`btn-secondary btn-sm${showDelivered ? ' active' : ''}`} data-read-safe
            onClick={() => setShowDelivered(v => !v)}
            title="Purchase orders delivered with no bill yet">
            <span className="material-symbols-rounded">local_shipping</span>
            {showDelivered ? 'Hide delivered, not billed' : 'Delivered, not billed'}
          </button>

          <ResetButton />

          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />

          {pagination.totalPages > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={changePage}
            />
          )}

        </div>

        {/* Possible duplicates, for every user, every time the list loads. */}
        <DuplicateBillsNotice onClosed={() => searchBills(pagination.page || 1)} />

        {showDelivered && <DeliveredNotBilledPanel onNavigate={onNavigate} />}

        {/* Wrapped so the horizontal cue can sit still against the
            visible edge — inside the scrolling box it would travel
            with the columns and leave the screen. */}
        <div className="scroll-more-wrap">
        <div className="table-container scroll-more-box" ref={tableScrollRef}>
          <table className="data-table bills-table resizable-table table-min-xl" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input type="checkbox" className="tick-check-input" id="select-all-bills"
                      checked={bills.length > 0 && selectedBills.size === bills.length}
                      onChange={toggleSelectAll} disabled={bills.length === 0} />
                    <label htmlFor="select-all-bills" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z" />
                        <polyline points="1 9 7 14 15 4" />
                      </svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="expand-col"><ResizeHandle colIndex={1} /></th>
                <th className="sortable" onClick={() => handleSort('supplier_reference')}>
                  Reference {sortField === 'supplier_reference' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className={`sortable ${cols.headerProps('supplier').className || ''}`.trim()}
                    onClick={() => handleSort('supplier')}
                    onContextMenu={cols.headerProps('supplier').onContextMenu}
                    title="Right-click to filter by the suppliers on this page">
                  Supplier {sortField === 'supplier' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th>PO #<ResizeHandle colIndex={4} /></th>
                <th className="sortable" onClick={() => handleSort('bill_date')}>
                  Bill Date {sortField === 'bill_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th className={`sortable ${cols.headerProps('due_state').className || ''}`.trim()}
                    onClick={() => handleSort('due_date')}
                    onContextMenu={cols.headerProps('due_state').onContextMenu}
                    title="Right-click to filter by overdue, not yet due or settled">
                  Due Date {sortField === 'due_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('total')}>
                  Total {sortField === 'total' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('amount_paid')}>
                  Paid {sortField === 'amount_paid' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={8} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('balance')}>
                  Balance {sortField === 'balance' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={9} />
                </th>
                <th className={`status-col ${cols.headerProps('status').className || ''}`.trim()}
                    onContextMenu={cols.headerProps('status').onContextMenu}
                    title="Right-click to filter by the statuses on this page">
                  Status<ResizeHandle colIndex={10} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={11} /></th>
              </tr>
            </thead>
            <tbody className={loading ? 'loading-fade' : ''}>
              {loading ? (
                <tr><td colSpan="12"><LoadingSpinner /></td></tr>
              ) : cols.rows.length === 0 ? (
                <tr><td colSpan="12" className="no-results">No bills found</td></tr>
              ) : (
                cols.rows.map((bill) => (
                  <React.Fragment key={bill.id}>
                    <tr
                      className={`expandable-row ${selectedBills.has(bill.id) ? 'selected' : ''} ${bill.is_archived ? 'row-archived' : ''}`.trim()}
                      onClick={() => toggleRowExpand(bill.id)}
                      onContextMenu={ctxMenu.onRowContextMenu(bill)}
                    >
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <label
                          className="tick-check-label"
                          style={{ justifyContent: 'center', gap: '0' }}
                          onMouseDown={dragSelect.onMouseDown(bill.id, selectedBills.has(bill.id))}
                          onMouseEnter={dragSelect.onMouseEnter(bill.id)}
                          onClick={(e) => e.preventDefault()}
                        >
                          <input type="checkbox" className="tick-check-input" id={`select-bill-${bill.id}`}
                            checked={selectedBills.has(bill.id)} onChange={() => toggleSelectBill(bill.id)} />
                          <label htmlFor={`select-bill-${bill.id}`} className="tick-check">
                            <svg width="16px" height="16px" viewBox="0 0 18 18">
                              <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z" />
                              <polyline points="1 9 7 14 15 4" />
                            </svg>
                          </label>
                        </label>
                      </td>
                      <td className="expand-cell">
                        <span className={`expand-icon ${expandedRows.has(bill.id) ? 'expanded' : ''}`}>▼</span>
                      </td>
                      <td>
                        <div className="bill-number-row">
                          <span className="bill-number-link"
                            onClick={(e) => { e.stopPropagation(); setSelectedBillId(bill.id); }}
                            style={{ cursor: 'pointer', color: 'var(--color-primary)', fontWeight: 'bold' }}>
                            {bill.supplier_reference || bill.bill_number || '-'}
                          </span>
                          {bill.transaction_type && bill.transaction_type !== 'BILL' && (
                            <span className={`type-badge type-${bill.transaction_type.toLowerCase()}`}>
                              {bill.transaction_type === 'CREDIT' ? 'CN' : bill.transaction_type === 'OVERPAYMENT' ? 'OP' : 'PP'}
                            </span>
                          )}
                          {bill.is_stock && (
                            <span className="stock-badge" title="Stock — bought for our own stock">Stock</span>
                          )}
                          {bill.is_queried && (
                            <span className="queried-badge" title="Queried">
                              <span className="material-symbols-rounded">help</span>
                            </span>
                          )}
                          {bill.attachment_path && (
                            <span className="material-symbols-rounded attachment-icon" title="PDF attached">attach_file</span>
                          )}
                        </div>
                        {bill.reference && <div className="bill-reference">{bill.reference}</div>}
                      </td>
                      <td>
                        {/* Names only. A supplier's account number identifies
                            them to Xero, not to the person reading the row. */}
                        <span className="name-with-badge">
                          <EntityLink type="contact" id={bill.account_id}>
                            {bill.supplier_name || bill.account_company_name || '-'}
                          </EntityLink>
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          // Goods POs, then any this bill is the third-party haulage for.
                          const linked = [
                            ...(Array.isArray(bill.linked_pos) ? bill.linked_pos : []),
                            ...(Array.isArray(bill.haulage_pos) ? bill.haulage_pos : []).map(p => ({ ...p, _haulage: true }))
                          ];
                          const chipText = (po) => (po._haulage ? haulageForLabel(po) : po.purchase_order_number);
                          // The PO number an invoice quoted, kept as text (not linked).
                          const poRef = bill.po_reference
                            ? <div className="bill-po-reference" title="PO Reference — quoted by the invoice, not linked">Ref {bill.po_reference}</div>
                            : null;
                          if (linked.length === 0) {
                            return poRef || <span style={{ color: 'var(--text-muted)' }}>-</span>;
                          }
                          // Show 1 badge if 1 or 2 POs; if more than 2, show 1 + "+N more"
                          if (linked.length <= 2) {
                            return (
                              <span className="po-badges-cell">
                                {linked.map(po => (
                                  <span
                                    key={`${po._haulage ? 'h' : 'g'}-${po.id}`}
                                    className="po-badge-inline"
                                    title={po._haulage
                                      ? `${haulageForLabel(po)}${po.contact_name ? ` (${po.contact_name})` : ''} — its cost and status are not changed by this bill`
                                      : `${po.purchase_order_number} — ${po.status || ''}`}
                                    onClick={() => setSelectedPOId(po.xero_purchase_order_id)}
                                  >
                                    {chipText(po)}
                                  </span>
                                ))}
                                {poRef}
                              </span>
                            );
                          }
                          const first = linked[0];
                          const remaining = linked.length - 1;
                          return (
                            <span className="po-badges-cell">
                              <span
                                className="po-badge-inline"
                                title={`${first.purchase_order_number} — ${first.status || ''}`}
                                onClick={() => setSelectedPOId(first.xero_purchase_order_id)}
                              >
                                {chipText(first)}
                              </span>
                              <span
                                className="po-badge-inline more"
                                title={linked.slice(1).map(chipText).join(', ')}
                                onClick={() => setSelectedBillId(bill.id)}
                              >
                                +{remaining} more
                              </span>
                              {poRef}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{formatDate(bill.bill_date)}</td>
                      <td style={bill.due_date && new Date(bill.due_date) < new Date() && bill.status !== 'Paid' ? { color: 'var(--color-error)', fontWeight: '600' } : {}}>
                        {formatDate(bill.due_date)}
                      </td>
                      <td className="currency">{formatCurrency(bill.total)}</td>
                      <td className="currency">{formatCurrency(bill.amount_paid)}</td>
                      <td className="currency" style={(bill.total - bill.amount_paid) > 0 && bill.status !== 'Paid' ? { fontWeight: '600' } : {}}>
                        {formatCurrency(bill.total - bill.amount_paid)}
                      </td>
                      <td className="status-col">
                        {bill.is_archived && <span className="archived-badge">Archived</span>}
                        <span className={getStatusBadgeClass(bill.status)}>{billStatusLabel(bill.status)}</span>
                        {(() => {
                          // A bill entered here, on its way to Xero.
                          const push = bill.source === 'local' ? billPushState(bill) : null;
                          if (push && push.key !== 'sent') {
                            return (
                              <span className={`bill-push-badge tone-${push.tone}`} title={[push.label, push.detail].filter(Boolean).join(' — ')}>
                                <span className="material-symbols-rounded">{push.icon}</span>
                              </span>
                            );
                          }
                          if (push && push.mismatches.length) {
                            return (
                              <span className="bill-push-badge tone-warning" title="In Xero, but Xero's copy differs — open the bill">
                                <span className="material-symbols-rounded">rule</span>
                              </span>
                            );
                          }
                          return !bill.xero_bill_id ? (
                            <span className="material-symbols-rounded broken-link-icon" title="Not linked to Xero">link_off</span>
                          ) : null;
                        })()}
                      </td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu actions={buildRowActions(bill)} />
                        {bill.source === 'xero' && !bill.xero_bill_id && (
                          <span className="material-symbols-rounded info-icon" title="Xero synced">cloud_download</span>
                        )}
                      </td>
                    </tr>
                    <tr className={`expanded-content ${expandedRows.has(bill.id) ? 'expanded' : ''}`}>
                      <td colSpan="12">
                        <div className="line-items-container">
                          {loadingLineItems[bill.id] ? (
                            <div className="loading-lines">Loading line items...</div>
                          ) : lineItems[bill.id]?.length > 0 ? (
                            <table className={`line-items-table${lineItems[bill.id].some(l => l.qty_facts?.po_line_quantity != null) ? ' has-po-col' : ''}`}>
                              <thead>
                                <tr>
                                  <th>Purchase description</th>
                                  <th className="text-right">Qty</th>
                                  {lineItems[bill.id].some(l => l.qty_facts?.po_line_quantity != null) && (
                                    <th className="text-right" title="The PO line this bills — supplied where recorded, else ordered">On the PO</th>
                                  )}
                                  <th className="text-right">Unit</th>
                                  <th className="text-right">Line</th>
                                  <th className="text-right">VAT</th>
                                  <th>Account</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lineItems[bill.id].map((line, idx) => (
                                  <tr key={idx}>
                                    {/* The item code as a badge left of the words (Cam, 21 Oct 2026). */}
                                    <td><ItemCodeDescription code={line.item_code} itemId={line.item_id} description={line.description} /></td>
                                    {/* Its own unit and tonnes, and its PO line's beside it
                                        (utils/qtyWithTonnes — Cam, 20 Oct 2026). */}
                                    <td className="text-right"><QtyWithTonnes quantity={line.quantity} line={line.qty_facts || {}} /></td>
                                    {lineItems[bill.id].some(l => l.qty_facts?.po_line_quantity != null) && (
                                      <td className="text-right">
                                        {line.qty_facts?.po_line_quantity != null ? (
                                          <QtyWithTonnes
                                            quantity={line.qty_facts.po_line_supplied_quantity ?? line.qty_facts.po_line_quantity}
                                            line={{ ...line.qty_facts, qty_unit: line.qty_facts.po_line_qty_unit, pallets: null }}
                                            label={line.qty_facts.po_line_supplied_quantity != null ? 'Supplied' : 'Ordered'}
                                          />
                                        ) : '-'}
                                      </td>
                                    )}
                                    <td className="text-right">{formatPrice(line.unit_amount)}</td>
                                    <td className="text-right">{formatCurrency(line.line_amount)}</td>
                                    <td className="text-right">{formatVatWithRate(line.tax_amount, line.tax_type, formatCurrency)}</td>
                                    <td>{line.account_code || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : expandedRows.has(bill.id) && !loadingLineItems[bill.id] ? (
                            <div className="no-lines">No line items found</div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
          <ScrollMore scrollRef={tableScrollRef} remaining={tableScroll.remainingX} direction="right" />
        </div>
      </div>

      {/* Table context menu (right-click) */}
      {poCostBillId && (
        <UpdatePoFromBillDialog
          billId={poCostBillId}
          onClose={() => { setPoCostBillId(null); searchBills(pagination.page || 1); }}
          onDone={(message) => setAlertDialog({ isOpen: true, type: 'success', message })}
        />
      )}
      {statusBill && (
        <BillStatusDialog
          bill={statusBill}
          onClose={() => { setStatusBill(null); searchBills(pagination.page || 1); }}
          onChanged={(data) => {
            const w = data?.warning || data?.status_change?.xero?.warning;
            setAlertDialog({
              isOpen: true,
              type: w ? 'warning' : 'success',
              message: w || `${statusBill.supplier_reference || statusBill.bill_number || 'Bill'}: ${data?.bill?.status || ''}${data?.bill?.is_queried ? ' (queried)' : ''}.`
            });
          }}
        />
      )}

      {documentOutput && (
        <DocumentOutputModal
          type="bill"
          ids={documentOutput.ids}
          initialMode={documentOutput.mode}
          user={user}
          onClose={() => setDocumentOutput(null)}
          onDone={(message) => {
            setDocumentOutput(null);
            if (message) setAlertDialog({ isOpen: true, message, type: 'success' });
          }}
        />
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={(ids) => [
          {
            key: 'bulk-draft',
            icon: 'edit_note',
            label: `Mark ${ids.size} bills as draft`,
            disabled: bulkActionLoading,
            onClick: () => handleBulkStatusChange('Draft')
          },
          {
            key: 'bulk-authorised',
            icon: 'task_alt',
            label: `Mark ${ids.size} bills as approved`,
            disabled: bulkActionLoading,
            onClick: () => handleBulkStatusChange('Authorised')
          },
          {
            key: 'bulk-export',
            icon: 'ios_share',
            label: `Export ${ids.size} bills`,
            onClick: () => openDocuments('download', ids)
          },
          {
            key: 'bulk-delete',
            icon: 'delete',
            label: `Delete ${ids.size} bills`,
            disabled: bulkActionLoading,
            onClick: handleBulkDelete
          },
          {
            key: 'clear-selection',
            icon: 'close',
            label: 'Clear selection',
            disabled: bulkActionLoading,
            onClick: () => setSelectedBills(new Set())
          }
        ]}
        actions={[
          {
            key: 'expand-all',
            icon: expandedRows.size === bills.length && bills.length > 0 ? 'unfold_less' : 'unfold_more',
            label: expandedRows.size === bills.length && bills.length > 0 ? 'Collapse all' : 'Expand all',
            disabled: loading || expandingAll || bills.length === 0,
            onClick: handleExpandAll
          },
          {
            key: 'select-all',
            icon: 'select_all',
            label: selectedBills.size === bills.length && bills.length > 0 ? 'Clear selection' : 'Select all',
            disabled: bills.length === 0,
            onClick: toggleSelectAll
          },
          {
            key: 'refresh',
            icon: 'refresh',
            label: 'Refresh results',
            disabled: loading,
            onClick: () => searchBills(pagination.page || 1)
          },
          {
            key: 'clear-filters',
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: handleClearFilters
          }
        ]}
      />

      {allocDocId && (
        <SupplierCreditAllocationModal
          docId={allocDocId}
          onClose={() => { setAllocDocId(null); searchBills(pagination.page || 1); }}
          onDone={(message) => setAlertDialog({ isOpen: true, type: 'success', message })}
        />
      )}

      {voidedBillId && (
        <VoidedDocumentDialog
          kind="bill"
          id={voidedBillId}
          onClose={() => { setVoidedBillId(null); searchBills(pagination.page || 1); }}
          onNavigate={onNavigate}
          returnTo="search-bills"
          onSent={(message) => setAlertDialog({ isOpen: true, type: 'success', message })}
        />
      )}

      {selectedBillId && ReactDOM.createPortal(
        <BillModal
          billId={selectedBillId}
          onClose={() => { setSelectedBillId(null); searchBills(pagination.page); }}
          onEdit={(id) => {
            sessionStorage.setItem('editBillId', id);
            setSelectedBillId(null);
            onNavigate && onNavigate('create-bill');
          }}
          onRefresh={() => searchBills(pagination.page)}
          onNavigate={onNavigate}
        />,
        document.body
      )}

      {selectedPOId && ReactDOM.createPortal(
        <PurchaseOrderModal
          purchaseOrderId={selectedPOId}
          onClose={() => { setSelectedPOId(null); searchBills(pagination.page); }}
        />,
        document.body
      )}

      {priorMonth.dialog}
      <AlertDialog isOpen={alertDialog.isOpen} message={alertDialog.message} type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })} />

      <ConfirmDialog isOpen={confirmDialog.isOpen} title={confirmDialog.title} message={confirmDialog.message}
        onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog({ ...confirmDialog, isOpen: false }); }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })} />

      <ConfirmDialog
        isOpen={showSyncWarning}
        title="Sync Bills from Xero"
        message="This will overwrite all local bill data with the latest from Xero for the period being synced. Any local changes not yet pushed to Xero will be lost."
        type="warning"
        confirmText="Sync"
        onConfirm={() => { setShowSyncWarning(false); pendingSyncAction?.(); }}
        onCancel={() => setShowSyncWarning(false)}
      />

      {/* Custom Date Sync Modal */}
      {showSyncModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', height: '600px' }}>
            <div className="modal-header">
              <h3>Sync Bills - Custom Dates</h3>
              <button className="btn-icon" onClick={() => setShowSyncModal(false)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-xl)' }}>
              <div className="form-group">
                <label>From Date</label>
                <DateInput value={syncDateFrom} onChange={(e) => setSyncDateFrom(e.target.value)} />
              </div>
              <div className="form-group">
                <label>To Date</label>
                <DateInput value={syncDateTo} onChange={(e) => setSyncDateTo(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end', marginTop: 'var(--spacing-lg)' }}>
                <button className="btn-secondary" onClick={() => setShowSyncModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={() => syncBills('custom')}>
                  <span className="material-symbols-rounded">sync</span> Start Sync
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ColumnFilterMenu cols={cols} />
    </div>
  );
}

export default SearchBills;
